import { motion } from 'framer-motion';
import { ChevronDown, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { Account, ScheduledItem, Transaction } from '../data/types';
import { computeSafeToSpend } from '../lib/cashPosition';
import { cn } from '../lib/cn';
import { formatCurrency } from '../lib/currency';
import { springGentle, springQuick, tapScale } from '../tokens';
import { Card } from './Card';

type Props = {
  accounts: Account[];
  scheduledItems: ScheduledItem[];
  transactions: Transaction[];
  /** Pay period start as ISO date "YYYY-MM-DD". */
  periodStart: string;
  /** Pay period end as ISO date "YYYY-MM-DD". */
  periodEnd: string;
  /** PYF still to be deducted between today and periodEnd. 0 for non-PYF. */
  pyfRemaining: number;
  currency: string;
};

/**
 * Cash-position visibility card. Pure projection over manual accounts +
 * scheduled bills + recent transactions via `computeSafeToSpend`. Default
 * collapsed; tap to expand the full breakdown.
 */
export function CashPositionCard({
  accounts,
  scheduledItems,
  transactions,
  periodStart,
  periodEnd,
  pyfRemaining,
  currency,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const r = useMemo(
    () =>
      computeSafeToSpend(
        accounts,
        scheduledItems,
        transactions,
        periodStart,
        periodEnd,
        pyfRemaining,
      ),
    [
      accounts,
      scheduledItems,
      transactions,
      periodStart,
      periodEnd,
      pyfRemaining,
    ],
  );

  if (accounts.length === 0) return null;

  const safeTone = r.safeToSpend < 0 ? 'text-negative' : 'text-text-primary';

  return (
    <Card elevation="none" className="mb-card-gap glass-md rounded-[20px]">
      <motion.button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        whileTap={tapScale}
        transition={springQuick}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-button bg-accent/10 inline-flex items-center justify-center text-accent shrink-0">
            <Wallet size={18} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
              Cash position
            </div>
            <div className="mt-0.5 text-[14px] text-text-primary">
              <span
                className={cn(
                  'font-mono tabular-nums font-semibold',
                  safeTone,
                )}
              >
                {formatCurrency(r.safeToSpend, { currency })}
              </span>{' '}
              <span className="text-text-secondary">safe to spend</span>
            </div>
          </div>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={springGentle}
          className="shrink-0 text-text-tertiary"
        >
          <ChevronDown size={18} strokeWidth={1.5} />
        </motion.div>
      </motion.button>

      <motion.div
        initial={false}
        animate={{
          height: expanded ? 'auto' : 0,
          opacity: expanded ? 1 : 0,
        }}
        transition={springGentle}
        className="overflow-hidden"
      >
        <div className="pt-4 mt-4 border-t border-black/[0.06] dark:border-white/[0.06] flex flex-col divide-y divide-black/[0.04] dark:divide-white/[0.04]">
          <Row label="Checking" value={r.checkingTotal} currency={currency} />
          <Row
            label="Savings"
            value={r.savingsTotal}
            currency={currency}
            hint="not subtracted"
          />
          <Row
            label="Credit cards"
            value={-Math.abs(r.creditTotal)}
            currency={currency}
            tone="negative"
            hint="info only"
          />
          {r.spendingSinceUpdate > 0 ? (
            <Row
              label="Spent this period"
              value={-r.spendingSinceUpdate}
              currency={currency}
              tone="negative"
            />
          ) : null}
          {r.paidBills > 0 ? (
            <Row
              label="Bills paid this period"
              value={-r.paidBills}
              currency={currency}
              tone="negative"
            />
          ) : null}
          <Row
            label="Upcoming bills"
            value={-r.upcomingBills}
            currency={currency}
            tone="negative"
          />
          {r.pyfRemaining > 0 ? (
            <Row
              label="PYF remaining"
              value={-r.pyfRemaining}
              currency={currency}
              tone="negative"
            />
          ) : null}
          <Row
            label="Safe to spend"
            value={r.safeToSpend}
            currency={currency}
            emphasis
            tone={r.safeToSpend < 0 ? 'negative' : 'positive'}
          />
        </div>
      </motion.div>
    </Card>
  );
}

type RowProps = {
  label: string;
  value: number;
  currency: string;
  tone?: 'negative' | 'positive';
  emphasis?: boolean;
  hint?: string;
};

function Row({ label, value, currency, tone, emphasis, hint }: RowProps) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3',
        emphasis ? 'py-3' : 'py-2',
      )}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <span
          className={cn(
            emphasis ? 'text-[14px]' : 'text-[13px]',
            'truncate',
            emphasis
              ? 'font-semibold text-text-primary'
              : 'text-text-secondary',
          )}
        >
          {label}
        </span>
        {hint ? (
          <span className="text-[11px] text-text-tertiary">({hint})</span>
        ) : null}
      </div>
      <span
        className={cn(
          'font-mono tnum shrink-0',
          emphasis ? 'text-[16px] font-semibold' : 'text-[13px]',
          tone === 'negative'
            ? 'text-negative'
            : tone === 'positive'
              ? 'text-positive'
              : 'text-text-primary',
        )}
      >
        {formatCurrency(value, { currency })}
      </span>
    </div>
  );
}
